import Drawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import type { Theme } from '@mui/material/styles';
import type { PropsWithChildren, ReactNode } from 'react';
import {
  SaveShortcutBoundary,
  type SaveShortcutStrategy,
} from '@src/shared/ui/SaveShortcutScope';
import SidePanelContent from './SidePanelContent';

type SidePanelProps = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  width?: number | string;
  topOffset?: number;
  rightOffset?: number;
  hideCloseButton?: boolean;
  disableBackdropClose?: boolean;
  fitContent?: boolean;
  shortcutBoundary?: boolean;
  shortcutStrategy?: SaveShortcutStrategy;
}>;

const StyleSheet = {
  paper: ({
    width,
    topOffset,
    rightOffset,
    fitContent,
  }: {
    width?: number | string;
    topOffset: number;
    rightOffset: number;
    fitContent: boolean;
  }) => ({
    border: '1px solid',
    borderColor: 'grey.100',
    borderRadius: 2,
    bottom: fitContent ? 'auto' : '10px',
    height: fitContent ? 'auto' : `calc(100% - ${topOffset + 10}px)`,
    maxHeight: `calc(100% - ${topOffset + 10}px)`,
    overflow: 'hidden',
    right: `${rightOffset}px`,
    top: `${topOffset}px`,
    width: width ?? ((theme: Theme) => theme.layout.aside.width),
  }),
};

function SidePanel({
  open,
  onClose,
  title,
  footer,
  width,
  topOffset = 0,
  rightOffset = 0,
  hideCloseButton = false,
  disableBackdropClose = false,
  fitContent = false,
  shortcutBoundary = true,
  shortcutStrategy = 'active',
  children,
}: SidePanelProps) {
  const handleClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (disableBackdropClose && reason === 'backdropClick') {
      return;
    }

    onClose();
  };

  const drawer = (
    <Drawer
      anchor="right"
      elevation={0}
      open={open}
      onClose={handleClose}
      variant="temporary"
      ModalProps={{
        disablePortal: true,
      }}
      slots={{
        transition: Fade,
      }}
      slotProps={{
        paper: {
          sx: StyleSheet.paper({ width, topOffset, rightOffset, fitContent }),
        },
        transition: {
          timeout: {
            enter: 180,
            exit: 120,
          },
        },
      }}
    >
      <SidePanelContent
        title={title}
        footer={footer}
        onClose={onClose}
        hideCloseButton={hideCloseButton}
      >
        {children}
      </SidePanelContent>
    </Drawer>
  );

  if (!shortcutBoundary) {
    return drawer;
  }

  return (
    <SaveShortcutBoundary
      active={open}
      priority="modal"
      strategy={shortcutStrategy}
    >
      {drawer}
    </SaveShortcutBoundary>
  );
}

export default SidePanel;
